'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

export interface StudioSelectOption {
  value: string;
  label: string;
  badge?: string; // 'HOT', 'NEW', 'PRO', etc.
  hint?: string;
  /** Small letter chip shown before the label (brand icon). */
  icon?: { letter: string; color: string };
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  options: StudioSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
}

export function StudioSelect({ value, onChange, options, placeholder, disabled, loading }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  if (loading && options.length === 0) {
    return (
      <div
        aria-busy="true"
        className="flex h-[46px] w-full animate-pulse items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-3 py-2.5"
      >
        <span className="flex items-center gap-2">
          <span className="h-5 w-5 rounded bg-white/15" />
          <span className="h-3 w-32 rounded-full bg-white/15" />
        </span>
        <span className="h-4 w-4 rounded bg-white/15" />
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className={`flex w-full items-center justify-between gap-2 rounded-2xl border border-white/15 bg-white/10 px-3 py-2.5 text-sm text-white transition hover:bg-white/15 ${
          disabled ? 'opacity-40 cursor-not-allowed' : ''
        } ${open ? 'border-fuchsia-400/60 ring-2 ring-fuchsia-500/20' : ''}`}
      >
        <span className="flex items-center gap-2 min-w-0 truncate">
          {selected ? (
            <>
              {selected.icon && <BrandIcon {...selected.icon} />}
              <span className="truncate">{selected.label}</span>
              {selected.badge && (
                <span className="shrink-0 rounded-full bg-orange-500/25 px-1.5 py-0.5 text-[9px] font-semibold text-orange-200">
                  {selected.badge}
                </span>
              )}
            </>
          ) : (
            <span className="text-white/50">{placeholder || t('studio.select.placeholder')}</span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-white/60 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-2xl border border-white/10 bg-[#1a0e2a]/95 shadow-2xl backdrop-blur-xl">
          <div
            data-lenis-prevent
            className="max-h-80 overflow-y-auto overscroll-contain py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            onWheelCapture={(event) => event.stopPropagation()}
            onTouchMoveCapture={(event) => event.stopPropagation()}
          >
            {loading && options.length === 0 && (
              <div className="space-y-3 px-3 py-4">
                <div className="h-4 w-4/5 animate-pulse rounded-full bg-white/10" />
                <div className="h-3 w-2/3 animate-pulse rounded-full bg-white/10" />
              </div>
            )}
            {!loading && options.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-white/60">
                {t('studio.select.noModels')}
              </div>
            )}
            {options.map((opt) => {
              const active = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition ${
                    active
                      ? 'bg-fuchsia-500/20 text-white'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span className="w-4 shrink-0 pt-0.5">
                    {active && <Check className="h-3.5 w-3.5 text-fuchsia-300" />}
                  </span>
                  {opt.icon && <BrandIcon {...opt.icon} />}
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="truncate">{opt.label}</span>
                      {opt.badge && (
                        <span
                          className={`text-[9px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${
                            opt.badge === 'HOT'
                              ? 'bg-orange-500/25 text-orange-200'
                              : opt.badge === 'NEW'
                              ? 'bg-emerald-500/25 text-emerald-200'
                              : 'bg-violet-500/25 text-violet-100'
                          }`}
                        >
                          {opt.badge}
                        </span>
                      )}
                    </span>
                    {opt.hint && (
                      <span className="mt-0.5 block truncate text-[10px] text-white/60">{opt.hint}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function BrandIcon({ letter, color }: { letter: string; color: string }) {
  return (
    <span
      className={`${color} shrink-0 w-5 h-5 rounded flex items-center justify-center text-[11px] font-bold text-white`}
    >
      {letter}
    </span>
  );
}
